/**
 * WordPress Dependencies
 */
import { get, isEmpty, head } from 'lodash';
import { __, sprintf } from '@wordpress/i18n';
import {
	SelectControl,
	TextControl,
	ToggleControl,
	__experimentalToggleGroupControl as ToggleGroupControl,
	__experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';

import isPremium from '../utils/is-premium';

import BlockTypeSearch from './block-type-search';

import { SUPPORTED_BLOCKS } from '../constants';

function PositionsControl(props) {
	const DEFAULT_POSITION = {
		locationType: 'post',
		placement: 'before',
		location: 'post-content',
		offset: 1,
		allowNested: true,
	};

	const [position, setPosition] = useState(() => {
		return isEmpty(props?.value)
			? DEFAULT_POSITION
			: {
					...DEFAULT_POSITION,
					...props.value,
			  };
	});

	useEffect(() => {
		props.onChange(position);
	}, [position]);

	const isPremiumPlugin = isPremium();

	const SUPPORTED_POST_TYPES = get(
		window,
		'automaticBlockInserter.supportedPostTypes',
		[]
	).map((postType) => ({
		value: postType,
		label: postType,
	}));

	const SUPPORTED_AREAS = [
		{
			value: 'post-content',
			label: __('Post Content', 'automatic-block-inserter'),
		},
	];

	const SUPPORTED_PLACEMENTS = [
		{
			value: 'before',
			label: __('Before', 'automatic-block-inserter'),
		},
		{
			value: 'after',
			label: __('After', 'automatic-block-inserter'),
		},
	];

	const SUPPORTED_LOCATION_TYPES = [
		{
			value: 'post',
			label: __('Post Area', 'automatic-block-inserter'),
		},
		{
			value: 'block',
			label: __('Block', 'automatic-block-inserter'),
		},
	];

	const locationTypeInstructions = sprintf(
		__(
			'This block will be inserted "%s" the %s below.',
			'automatic-block-inserter'
		),
		position.placement,
		position.locationType === 'block'
			? __('selected block type', 'automatic-block-inserter')
			: __('selected post area', 'automatic-block-inserter')
	);

	const initialArea = get(SUPPORTED_AREAS, [0, 'value']);
	const initialBlockType = get(SUPPORTED_BLOCKS(), [0, 'value']);

	const currentPostType = get(position, 'postType', '');
	const isCurrentlyPositive = position.offset > 0;

	return (
		<>
			<SelectControl
				options={[
					{
						label: __('All Post Types', 'automatic-block-inserter'),
						value: '',
					},
					...SUPPORTED_POST_TYPES,
				]}
				value={currentPostType}
				onChange={(newPostType) =>
					setPosition({
						...position,
						postType: newPostType,
					})
				}
				label={__('Post Type', 'automatic-block-inserter')}
				help={__(
					'Select a post type where this block should be displayed according to your selected position below.',
					'automatic-block-inserter'
				)}
			/>
			<ToggleGroupControl
				isBlock
				label={__('Location Type')}
				help={locationTypeInstructions}
				onChange={(newLocationType) => {
					const newPosition = {
						...position,
						locationType: newLocationType,
						location:
							newLocationType === 'block'
								? initialBlockType
								: initialArea,
					};

					setPosition(newPosition);
				}}
				value={position.locationType}
			>
				{SUPPORTED_LOCATION_TYPES.map((locationType, index) => {
					return (
						<ToggleGroupControlOption
							key={index}
							value={locationType.value}
							label={locationType.label}
						/>
					);
				})}
			</ToggleGroupControl>
			<SelectControl
				options={SUPPORTED_PLACEMENTS}
				value={position.placement}
				onChange={(newPlacement) =>
					setPosition({
						...position,
						placement: newPlacement,
					})
				}
				label={__('Placement', 'automatic-block-inserter')}
				help={__(
					'Select a placement of the content.',
					'automatic-block-inserter'
				)}
			/>
			{position.locationType === 'block' ? (
				<>
					<ToggleControl
						label={__(
							'Match any block type',
							'automatic-block-inserter'
						)}
						checked={position.location === 'any'}
						onChange={() =>
							setPosition({
								...position,
								location:
									position.location === 'any' ? '' : 'any',
							})
						}
						help={__(
							'If enabled, the block will be inserted before or after any block type on the page.',
							'automatic-block-inserter'
						)}
					/>
					{position.location !== 'any' && (
						<BlockTypeSearch
							value={position.location}
							onChange={(newLocation) => {
								setPosition({
									...position,
									location: newLocation,
								});
							}}
						/>
					)}
					<br />
					<TextControl
						type="number"
						value={position.offset}
						label={__('Offset', 'automatic-block-inserter')}
						help={
							isCurrentlyPositive
								? sprintf(
										__(
											'This block will be inserted %s the %d selected block type on the page.',
											'automatic-block-inserter'
										),
										position.placement,
										position.offset
								  )
								: sprintf(
										__(
											'This block will be inserted %s the %d last selected block type on the page from the bottom.',
											'automatic-block-inserter'
										),
										position.placement,
										Math.abs(position.offset)
								  )
						}
						onChange={(newOffsetRaw) => {
							const newOffset = Number(newOffsetRaw);
							const nextAvailableOffset = isCurrentlyPositive
								? -1
								: 1;

							setPosition({
								...position,
								offset:
									0 === newOffset
										? nextAvailableOffset
										: newOffset,
							});
						}}
						{...(!isPremiumPlugin && {
							min: 1,
						})}
					/>
					{isPremiumPlugin && (
						<ToggleControl
							label={__(
								'Top Level Blocks',
								'automatic-block-inserter'
							)}
							checked={!position.allowNested}
							onChange={() =>
								setPosition({
									...position,
									allowNested: !position.allowNested,
								})
							}
							help={__(
								'Enabling this will limit block insertion to non-nested blocks. Any blocks within another block, such as a group, will not be considered.',
								'automatic-block-inserter'
							)}
						/>
					)}
				</>
			) : (
				<SelectControl
					options={SUPPORTED_AREAS}
					value={position.location}
					onChange={(newPostArea) =>
						setPosition({ ...position, location: newPostArea })
					}
					label={__('Select Post Area', 'automatic-block-inserter')}
				/>
			)}
		</>
	);
}

export default PositionsControl;
